import { Modal } from '@mantine/core'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { getApiError } from '@/api/helpers/apiError'
import { DataUsageIndicator } from '@/components/subscriptions/DataUsageIndicator/DataUsageIndicator'
import { DataUsageIndicatorWrapper } from '@/components/subscriptions/DataUsageIndicator/DataUsageIndicatorWrapper'
import { useFeatureUsage } from '@/components/subscriptions/useFeatureUsage'
import { useUsageLimitModal } from '@/components/subscriptions/useUsageLimitModal'
import {
  useGetInferenceDetails,
  useStartInference
} from '@/queries/inferenceQueries'
import { showToast } from '@/theme/notifications'
import { InferenceStatus } from '@/types/inference'
import { MLModel, MLModelType } from '@/types/model'
import { bytesToGigabytes, roundToDecimals } from '@/utils/file'
import { TestModelPredictionsStep } from './TestModelPredictionStep'
import { TestModelUploadMedia } from './TestModelUploadMedia'

type InferenceModalProps = {
  opened: boolean
  applicationId?: string
  model?: MLModel
  resolution?: string
  onClose: () => void
}

export const InferenceModal = ({
  opened,
  applicationId,
  model,
  resolution,
  onClose
}: InferenceModalProps) => {
  const intl = useIntl()
  const [step, setStep] = useState(1)
  const [inferenceId, setInferenceId] = useState<string>('')
  const [inferredMedia, setInferredMedia] = useState<string[]>([])

  const { usage, refreshFeatureUsage } = useFeatureUsage('model_test_data')
  const { openUsageLimitModal } = useUsageLimitModal()

  const { mutateAsync: startInference, isPending: isStartPending } =
    useStartInference()

  const { data: inferenceData, isError } = useGetInferenceDetails({
    inferenceId,
    enabled: inferenceId !== ''
  })

  const inferenceDetails = inferenceData?.result

  const isPending =
    isStartPending || inferenceDetails?.status === InferenceStatus.InProgress

  useEffect(() => {
    if (inferenceDetails) {
      if (inferenceDetails.status === InferenceStatus.Success) {
        showToast(intl.formatMessage({ id: 'models.test.success' }), 'success')
        setInferredMedia(inferenceDetails.media_files || [])
        setStep(2)
        setInferenceId('')
        void refreshFeatureUsage()
      }

      if (inferenceDetails?.status === InferenceStatus.Failed || isError) {
        showToast(intl.formatMessage({ id: 'models.test.error' }), 'error')
        setInferenceId('')
      }
    }
  }, [inferenceDetails, intl, isError, refreshFeatureUsage])

  const handleResetData = () => {
    setInferenceId('')
    setInferredMedia([])
    setStep(1)
  }

  const handleOnClose = () => {
    onClose()

    if (step === 2) {
      handleResetData()
    }
  }

  const handleStartInference = async (acceptedFiles: File[]) => {
    if (!applicationId || !model) {
      return
    }

    const totalSizeGb = bytesToGigabytes(
      acceptedFiles.reduce((acc, file) => acc + file.size, 0)
    )

    const hasRemainingData =
      !usage ||
      usage.isUnlimited ||
      usage.remaining >= roundToDecimals(totalSizeGb, 8)

    if (!hasRemainingData) {
      openUsageLimitModal('model_test_data', {
        maxModelTestData: [usage.limit, usage.unit]
      })

      return
    }

    try {
      const { data } = await startInference({
        applicationId,
        modelId: model.id,
        mediaFiles: acceptedFiles,
        resolution
      })

      setInferenceId(data.results.inference_id)
      showToast(intl.formatMessage({ id: 'models.test.loading' }), 'info')
    } catch (err) {
      const { errorMessage } = getApiError(err)

      const message =
        errorMessage || intl.formatMessage({ id: 'models.test.error' })

      showToast(message, 'error')
    }
  }

  return (
    <Modal
      size={step === 1 ? '60rem' : '100rem'}
      opened={opened}
      title={
        <FormattedMessage
          id="models.test.popup.title"
          values={{ name: model?.name }}
        />
      }
      centered
      onClose={handleOnClose}
    >
      {step === 1 ? (
        <TestModelUploadMedia
          isStage2={model?.model_type === MLModelType.Secondary}
          isInferencePending={isPending}
          leftSection={
            usage &&
            !usage.isUnlimited && (
              <DataUsageIndicatorWrapper w={320} p={0} bg="transparent">
                <DataUsageIndicator
                  title={
                    <FormattedMessage id="subscriptions.usage.modelTestData" />
                  }
                  tooltip={
                    <FormattedMessage id="subscriptions.usage.modelTestData.description" />
                  }
                  used={usage.used}
                  total={usage.limit}
                  unit={usage.unit}
                  isMonthly={usage.period === 'monthly'}
                />
              </DataUsageIndicatorWrapper>
            )
          }
          onStartInference={(acceptedFiles) =>
            void handleStartInference(acceptedFiles)
          }
        />
      ) : (
        <TestModelPredictionsStep
          media_paths={inferredMedia}
          onClose={handleOnClose}
          onBack={handleResetData}
        />
      )}
    </Modal>
  )
}
